import { cn } from '@/utils';
import { RoleplayCardHistoryItemActions } from './subcomponents/RoleplayCardHistoryItemActions';
import { RoleplayCardHistoryItemDateTime } from './subcomponents/RoleplayCardHistoryItemDateTime';
import { RoleplayCardHistoryItemScoreTag } from './subcomponents/RoleplayCardHistoryItemScoreTag';
import { roleplayCardHistoryItemStyles } from '../../RoleplayCard.styles';
import { RoleplayCardProps } from '../../RoleplayCard';
import { TScoreType, TSimulationType } from '@/types';
import React from 'react';

export interface RoleplayCardHistoryItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The session ID for this history item.
   */
  scoreId?: string;

  /**
   * The date the roleplay session was completed.
   */
  date: string;

  /**
   * The time the roleplay session was completed.
   */
  time: string;

  /**
   * The score for this session.
   */
  score: number;

  /**
   * The max score for this session.
   */
  scoreMax?: number;

  /**
   * Callback for when the "Share with manager" button is clicked.
   */
  onShareWithManager?: RoleplayCardProps['onShareWithManager'];

  /**
   * Callback for when the "View scorecard" button is clicked.
   */
  onViewScoreCard?: RoleplayCardProps['onViewScoreCard'];

  /**
   * Whether this is the last item in the list (removes bottom border).
   */
  isLastItem?: boolean;

  /**
   * Loading state to indicate whether the history item data is still being fetched or processed.
   */
  isLoading?: boolean;

  /**
   * The type of score (e.g., 'numeric', 'pass/fail') to determine display formatting.
   */
  type?: TScoreType;

  /**
   * Type of simulation (role-play, practice, certification) to adjust display formatting if needed.
   */
  simulationType?: TSimulationType;

  /**
   * Whether the session was a pass or fail, used for practice and certification types.
   * For numeric scores, this can be derived from the score and skill level.
   * For practice and certification types, this should be explicitly provided.
   * If not provided for practice/certification, it will default to false (fail).
   * For role-play types, this can be derived from the score and skill level.
   */
  pass?: boolean;

  /**
   * User ID associated with this scorecard, required for sharing functionality.
   */
  userId?: string;

  /**
   * Scorecard ID associated with this score, required for sharing functionality.
   */
  scoreCardId?: number;

  /**
   * Session ID associated with this score, required for sharing functionality.
   */
  sessionId?: number;

  /**
   * Whether this score has already been shared with the manager (used to disable the share button).
   */
  sharedWithManager: boolean;
}

/**
 * RoleplayCardHistoryItem displays a single roleplay session history entry
 * with date, time, score, optional score delta, and action buttons.
 */
export const RoleplayCardHistoryItem: React.FC<RoleplayCardHistoryItemProps> = ({
  className,
  date,
  isLastItem = false,
  isLoading = false,
  onShareWithManager,
  onViewScoreCard,
  pass,
  score,
  scoreCardId,
  scoreId,
  scoreMax,
  sessionId,
  sharedWithManager,
  simulationType,
  time,
  type,
  userId,
  ...props
}) => {
  return (
    <div
      className={cn(
        roleplayCardHistoryItemStyles.base,
        { [roleplayCardHistoryItemStyles.lastItem]: isLastItem },
        className,
      )}
      data-score-id={scoreId}
      data-testid={`roleplay-card-history-item-${scoreId}`}
      {...props}
    >
      {/* Left side - Date, time, score, and delta */}
      <div className={roleplayCardHistoryItemStyles.sessionInfo}>
        <RoleplayCardHistoryItemDateTime date={date} time={time} isLoading={isLoading} />
        <RoleplayCardHistoryItemScoreTag
          isLoading={isLoading}
          pass={pass}
          score={score}
          scoreMax={scoreMax}
          simulationType={simulationType}
        />
      </div>

      {/* Right side - Action buttons */}
      <div className={roleplayCardHistoryItemStyles.sessionActions}>
        <RoleplayCardHistoryItemActions
          isLoading={isLoading}
          onShareWithManager={() => onShareWithManager?.(scoreId ?? '')}
          onViewScoreCard={() => onViewScoreCard?.(scoreId ?? '')}
          scoreCardId={scoreCardId}
          sessionId={sessionId}
          sharedWithManager={sharedWithManager}
          simulationType={simulationType}
          userId={userId}
        />
      </div>
    </div>
  );
};

RoleplayCardHistoryItem.displayName = 'RoleplayCardHistoryItem';

export default RoleplayCardHistoryItem;
